/**
 * Updates product card stock status indicators when the user selects or deselects a store.
 * Listens to the global `on:store:changed` event dispatched by the store selector.
 *
 * Each `.card__stock-status` element carries data attributes for client-side recomputation:
 *   data-inventories       JSON map of locationId → quantity
 *   data-threshold-low     Low-stock threshold
 *   data-threshold-very-low Very-low-stock threshold
 *   data-in-stock-count    Pre-computed number of in-stock stores
 */

(function () {
  'use strict';

  var STOCK_CLASSES = ['in-stock', 'low-stock', 'very-low-stock', 'out-of-stock'];
  var syncScheduled = false;

  /**
   * Determines the stock CSS class based on quantity and thresholds.
   * Mirrors the logic in store-selector-stock-state.liquid.
   */
  function getStockClass(qty, thresholdLow, thresholdVeryLow) {
    if (qty <= 0) return 'out-of-stock';

    if (qty <= thresholdVeryLow) return 'very-low-stock';

    if (qty <= thresholdLow) return 'low-stock';

    return 'in-stock';
  }

  /**
   * Replaces the store-selector stock class on a line element.
   */
  function setLineStockClass(line, stockClass) {
    for (var i = 0; i < STOCK_CLASSES.length; i++) {
      line.classList.remove('store-selector__stock--' + STOCK_CLASSES[i]);
    }

    line.classList.add('store-selector__stock--' + stockClass);
  }

  /**
   * Handles the global store-changed event.
   */
  function handleStoreChanged(evt) {
    var detail = evt.detail || {};
    var store = detail.store;
    var isClickCollect = detail.fulfillmentMode === 'click_collect' && store && store.id;

    var elements = document.querySelectorAll('.card__stock-status');

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      var storeLine = el.querySelector('.js-stock-store-line');
      if (!storeLine) continue;

      var textEl = storeLine.querySelector('.card__stock-text');
      if (!textEl) continue;

      var thresholdLow = parseInt(el.dataset.thresholdLow, 10);
      var thresholdVeryLow = parseInt(el.dataset.thresholdVeryLow, 10);
      var normalizedThresholdLow = isNaN(thresholdLow) ? 10 : thresholdLow;
      var normalizedThresholdVeryLow = isNaN(thresholdVeryLow) ? 2 : thresholdVeryLow;

      if (isClickCollect) {
        // Show specific store stock
        var inventories;
        try {
          inventories = JSON.parse(el.dataset.inventories || '{}');
        } catch {
          inventories = {};
        }

        var storeId = String(store.id);
        var qty = Number(inventories[storeId]) || 0;
        var stockClass = getStockClass(qty, normalizedThresholdLow, normalizedThresholdVeryLow);

        setLineStockClass(storeLine, stockClass);
        textEl.textContent = store.name;
      } else {
        // Revert to "In stock at X stores" mode
        var count = parseInt(el.dataset.inStockCount, 10) || 0;
        var storesClass = count > 0 ? 'in-stock' : 'out-of-stock';
        var strings = theme.strings.storeSelector || {};
        var label = count > 0 ? (strings.inStockInStores || '').replace('[count]', count) : strings.outOfStockInStores || '';

        setLineStockClass(storeLine, storesClass);
        textEl.textContent = label;
      }
    }
  }

  function getServerSelectionDetail() {
    var element = document.querySelector('.card__stock-status[data-selected-store-id]');
    if (!element) return null;

    var storeId = String(element.dataset.selectedStoreId || '').trim();
    if (!storeId) return null;

    return {
      store: {
        id: storeId,
        name: String(element.dataset.selectedStoreName || ''),
      },
      fulfillmentMode: 'click_collect',
    };
  }

  function getStoredSelectionDetail() {
    var selection = window.StoreSelector && typeof window.StoreSelector.readSelection === 'function' ? window.StoreSelector.readSelection() : null;

    if (selection && selection.mode === 'click_collect' && selection.store && selection.store.id) {
      return {
        store: selection.store,
        fulfillmentMode: 'click_collect',
      };
    }

    var serverSelection = getServerSelectionDetail();
    if (serverSelection) return serverSelection;

    return {
      store: selection && selection.store ? selection.store : null,
      fulfillmentMode: selection && selection.mode ? selection.mode : 'delivery',
    };
  }

  function syncFromStoredSelection() {
    handleStoreChanged({
      detail: getStoredSelectionDetail(),
    });
  }

  function queueStoredSelectionSync() {
    if (syncScheduled) return;

    syncScheduled = true;
    var flush = function () {
      syncScheduled = false;
      syncFromStoredSelection();
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(flush);
      return;
    }

    window.setTimeout(flush, 16);
  }

  function nodeContainsStockStatus(node) {
    if (!node || node.nodeType !== 1) return false;

    return node.matches('.card__stock-status') || Boolean(node.querySelector('.card__stock-status'));
  }

  function observeCardStatusMutations() {
    if (!('MutationObserver' in window) || !document.body) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type !== 'childList' || !mutation.addedNodes.length) continue;

        for (var j = 0; j < mutation.addedNodes.length; j++) {
          if (!nodeContainsStockStatus(mutation.addedNodes[j])) continue;

          queueStoredSelectionSync();
          return;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener('on:store:changed', handleStoreChanged);
  document.addEventListener('on:facet-filters:updated', queueStoredSelectionSync);
  observeCardStatusMutations();
  syncFromStoredSelection();
})();
